import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  Home, DollarSign, Handshake, Star, ArrowRight, CheckCircle2 
} from 'lucide-react';
import AnimatedSection, { 
  SectionDivider, 
  StaggerContainer, 
  StaggerItem,
  SectionTransition 
} from '../components/AnimatedSection';
import { GlassSection, PremiumGlassSection } from '../components/GlassSection';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '../components/ui/carousel';

const HomePage = () => {
  const processSteps = [
    {
      icon: Home,
      title: 'Tell Us About Your Home',
      description: 'Provide basic details about your property in minutes. No paperwork, no stress.',
    },
    {
      icon: DollarSign,
      title: 'Get a Fast Cash Offer',
      description: 'We evaluate your home and make a fair cash offer within 24 hours.',
    },
    {
      icon: Handshake,
      title: 'Close on Your Terms',
      description: 'Choose the timeline that works best for you. We handle everything.',
    },
  ];

  // Realistic American suburban homes
  const galleryImages = [
    {
      url: 'https://images.unsplash.com/photo-1760649004090-b70cea9cce39?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      location: 'Columbus, OH',
      note: 'Classic brick two-story',
    },
    {
      url: 'https://images.unsplash.com/photo-1625603736199-775425d2890a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      location: 'Portland, OR',
      note: 'Charming craftsman style',
    },
    {
      url: 'https://images.unsplash.com/photo-1624741212832-7ec9baefa0ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      location: 'New Bern, NC',
      note: 'Traditional southern home',
    },
    {
      url: 'https://images.unsplash.com/photo-1765765234094-bc009a3bba62?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      location: 'Cleveland, OH',
      note: 'Well-maintained colonial',
    },
    {
      url: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      location: 'Austin, TX',
      note: 'Modern suburban ranch',
    },
    {
      url: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      location: 'Denver, CO',
      note: 'Family neighborhood gem',
    },
  ];

  const testimonials = [
    {
      name: 'Anna Richardson',
      location: 'Orlando, FL',
      text: 'Green Palm Consulting made selling our home so easy – no fees, no stress! They handled everything professionally.',
      rating: 5,
    },
    {
      name: 'John Martinez',
      location: 'Austin, TX',
      text: 'Quick offer and smooth process, highly recommended! Closed in just 2 weeks exactly when I needed to.',
      rating: 5,
    },
    {
      name: 'Lisa Chen',
      location: 'Miami, FL',
      text: 'We got a fair price and everything was handled professionally. The team was responsive and transparent throughout.',
      rating: 5,
    },
    {
      name: 'Robert Thompson',
      location: 'Phoenix, AZ',
      text: 'After trying to sell traditionally for months, Green Palm gave us a cash offer within 24 hours. Life-changing!',
      rating: 5,
    },
    {
      name: 'Sarah Williams',
      location: 'Denver, CO',
      text: 'No hidden fees, no surprises. The amount they quoted was exactly what we received. Truly trustworthy!',
      rating: 5,
    },
  ];

  const benefits = [
    'No agent commissions or fees',
    'Close in as little as 7 days',
    'We buy homes in any condition',
    'No repairs needed before sale',
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-20"
      >
        {/* Background Image with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="American Suburban Home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-hero-overlay" />
          
          {/* Floating decorative shapes */}
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-emerald/10 rounded-full blur-3xl animate-pulse-glow" />
          <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-gold/10 rounded-full blur-3xl animate-pulse-glow animate-delay-200" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.span 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-block bg-gold/20 text-gold font-subheading font-medium text-sm px-4 py-2 rounded-full mb-6 border border-gold/30 backdrop-blur-sm"
              >
                Trusted Home Buyers Nationwide
              </motion.span>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                Sell Your Home Fast.
                <motion.span 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="block text-gold"
                >
                  No Agents. No Fees.
                </motion.span>
              </h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="font-subheading text-lg md:text-xl text-white/85 mb-8 leading-relaxed"
              >
                Green Palm Consulting helps homeowners across the USA sell quickly and hassle-free. 
                Get a fair cash offer within 24 hours.
              </motion.p>

              {/* Benefits List */}
              <ul className="space-y-3 mb-10">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                    className="flex items-center gap-3 text-white/90"
                  >
                    <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0" />
                    <span className="font-body text-sm md:text-base">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/get-offer"
                data-testid="hero-cta-button"
                className="btn-premium inline-flex items-center justify-center bg-emerald hover:bg-emerald-dark text-white font-bold text-base px-8 py-4 rounded-full group"
              >
                Get Your Offer
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-secondary-button"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white hover:text-darkgreen font-bold text-base px-8 py-4 rounded-full transition-all duration-400 hover:scale-105"
              >
                See How It Works
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Transition: Hero to Process */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative z-20 -mt-1"
      >
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0 60L60 52C120 44 240 28 360 24C480 20 600 28 720 32C840 36 960 36 1080 32C1200 28 1320 20 1380 16L1440 12V0H1380C1320 0 1200 0 1080 0C960 0 840 0 720 0C600 0 480 0 360 0C240 0 120 0 60 0H0V60Z" fill="#0A3D2D"/>
        </svg>
      </motion.div>

      {/* 3-Step Process Section */}
      <GlassSection darkSection>
        <section 
          data-testid="process-section"
          className="py-24 md:py-32 relative overflow-hidden -mt-1"
          style={{
            background: 'linear-gradient(180deg, #0A3D2D 0%, #062B1F 50%, #0A3D2D 100%)'
          }}
        >
          {/* Radial gradient overlays for depth */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse 80% 50% at 20% 20%, rgba(4, 99, 7, 0.3) 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)'
          }} />
          
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-72 h-72 bg-emerald/20 rounded-full blur-3xl animate-float-slow" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl animate-float-slow animate-delay-300" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald/5 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <AnimatedSection className="text-center mb-16" scale blur>
              <span className="inline-block bg-gold/20 text-gold font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4 border border-gold/30">
                Simple & Fast
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
                How It Works
              </h2>
              <p className="font-subheading text-base md:text-lg text-white/70 max-w-2xl mx-auto">
                Selling your home has never been easier. Our streamlined process gets you a cash offer fast.
              </p>
            </AnimatedSection>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6" staggerDelay={0.15}>
            {processSteps.map((step, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  data-testid={`process-step-${index + 1}`}
                  className="relative bg-white border border-gray-100 rounded-2xl p-8 shadow-sm card-premium h-full"
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-10 h-10 bg-emerald text-white rounded-full flex items-center justify-center font-heading font-bold text-lg shadow-lg">
                    {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <motion.div 
                    className="w-16 h-16 bg-emerald/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-emerald transition-all duration-300"
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(4, 99, 7, 1)' }}
                  >
                    <step.icon className="w-8 h-8 text-emerald" />
                  </motion.div>
                  
                  <h3 className="font-heading text-xl font-bold text-darkgreen mb-3">
                    {step.title}
                  </h3>
                  <p className="font-body text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
          </div>
        </section>
      </GlassSection>

      {/* Transition: Process to Gallery */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative z-20 -mt-1"
      >
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0 0L60 8C120 16 240 32 360 36C480 40 600 32 720 28C840 24 960 24 1080 28C1200 32 1320 40 1380 44L1440 48V60H1380C1320 60 1200 60 1080 60C960 60 840 60 720 60C600 60 480 60 360 60C240 60 120 60 60 60H0V0Z" fill="#0A3D2D"/>
        </svg>
      </motion.div>

      {/* Gallery Section */}
      <GlassSection>
        <section 
          data-testid="gallery-section"
          className="py-24 md:py-32 bg-gradient-section-alt relative overflow-hidden"
      >
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-80 h-80 bg-emerald/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gold/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16" scale blur>
            <span className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4">
              Our Portfolio
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-darkgreen mb-4">
              Homes We've Purchased
            </h2>
            <p className="font-subheading text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              We buy homes of all types and conditions across the United States.
            </p>
          </AnimatedSection>

          <AnimatedSection direction="up" duration={0.8}>
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {galleryImages.map((image, index) => (
                  <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <motion.div 
                      data-testid={`gallery-item-${index}`}
                      className="relative rounded-2xl overflow-hidden group cursor-pointer parallax-container"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.4 }}
                    >
                      <img
                        src={image.url}
                        alt={`Home in ${image.location}`}
                        className="w-full h-72 object-cover parallax-image"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-darkgreen/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-400">
                        <p className="font-heading font-bold text-white text-lg">{image.location}</p>
                        <p className="text-white/80 text-sm">{image.note}</p>
                      </div>
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious 
                data-testid="gallery-prev-button"
                className="left-4 bg-white/90 hover:bg-white border-0 shadow-lg hover:scale-110 transition-transform" 
              />
              <CarouselNext 
                data-testid="gallery-next-button"
                className="right-4 bg-white/90 hover:bg-white border-0 shadow-lg hover:scale-110 transition-transform" 
              />
            </Carousel>
          </AnimatedSection>
        </div>
        </section>
      </GlassSection>

      {/* Transition: Gallery to Testimonials - Animated divider */}
      <div className="relative bg-gradient-section-alt">
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="max-w-4xl mx-auto px-6"
        >
          <div className="h-px bg-gradient-to-r from-transparent via-emerald/40 to-transparent" />
        </motion.div>
      </div>

      {/* Testimonials Section */}
      <GlassSection>
        <section 
          data-testid="testimonials-section"
          className="py-24 md:py-32 bg-gradient-section relative overflow-hidden"
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/2 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 animate-float-slow" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <AnimatedSection className="text-center mb-16" scale blur>
              <span className="inline-block bg-gold/20 text-gold font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4 border border-gold/30">
                Happy Homeowners
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-darkgreen mb-4">
                What Our Clients Say
              </h2>
              <p className="font-subheading text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                Join thousands of satisfied homeowners who chose the stress-free way to sell.
              </p>
            </AnimatedSection>

            <AnimatedSection direction="up" duration={0.8}>
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-full"
              >
                <CarouselContent className="-ml-4">
                  {testimonials.map((testimonial, index) => (
                    <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                      <motion.div 
                      data-testid={`testimonial-item-${index}`}
                      className="bg-white rounded-2xl p-8 h-full border border-gray-100 card-premium"
                      whileHover={{ y: -5 }}
                    >
                      {/* Stars */}
                      <div className="flex gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                        ))}
                      </div>
                      
                      {/* Quote */}
                      <p className="font-body text-gray-700 mb-6 leading-relaxed">
                        "{testimonial.text}"
                      </p>
                      
                      {/* Author */}
                      <div>
                        <p className="font-heading font-bold text-darkgreen">{testimonial.name}</p>
                        <p className="text-sm text-gray-500">{testimonial.location}</p>
                      </div>
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious 
                data-testid="testimonials-prev-button"
                className="left-4 bg-white hover:bg-emerald hover:text-white border-emerald/20 shadow-lg hover:scale-110 transition-all" 
              />
              <CarouselNext 
                data-testid="testimonials-next-button"
                className="right-4 bg-white hover:bg-emerald hover:text-white border-emerald/20 shadow-lg hover:scale-110 transition-all" 
              />
            </Carousel>
            </AnimatedSection>
          </div>
        </section>
      </GlassSection>

      {/* Transition: Testimonials to CTA - Wave transition */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative z-20 -mb-1"
      >
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0 80L48 74.7C96 69.3 192 58.7 288 53.3C384 48 480 48 576 53.3C672 58.7 768 69.3 864 69.3C960 69.3 1056 58.7 1152 53.3C1248 48 1344 48 1392 48L1440 48V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V80Z" fill="#0A3D2D"/>
        </svg>
      </motion.div>

      {/* CTA Section */}
      <GlassSection darkSection>
        <section 
          data-testid="cta-section"
          className="py-24 md:py-32 bg-darkgreen relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-96 h-96 bg-emerald/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-pulse-glow" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/15 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 animate-pulse-glow animate-delay-200" />
          </div>

          <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
            <AnimatedSection scale blur>
              <motion.h2 
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              >
                Ready to Sell Your Home?
              </motion.h2>
              <motion.p 
                className="font-subheading text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
              >
                Get a no-obligation cash offer within 24 hours. No fees, no commissions, no hassle.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <Link
                  to="/get-offer"
                  data-testid="cta-section-button"
                  className="btn-premium inline-flex items-center justify-center bg-emerald hover:bg-emerald-dark text-white font-bold text-lg px-10 py-5 rounded-full group"
                >
                Get Your Free Offer
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            </AnimatedSection>
          </div>
        </section>
      </GlassSection>
    </div>
  );
};

export default HomePage;
